import React, { useState, useEffect } from 'react';
import { FiEdit2, FiTrash2, FiShoppingCart, FiPlusCircle, FiMinusCircle, FiUser } from 'react-icons/fi';
import { Checkbox, Tooltip, notification } from 'antd';

const PropertyCard = ({ 
  property, 
  onDelete, 
  cart, 
  removeFromCart, 
  addToFromCart, 
  onEdit,
  onRecurringOrders,
  onEditDateTime,
  isSelected = false,
  onSelect,
  showOwner = false,
  ownerName = "",
  onImpersonateUser,
  isAdmin = false,
  onToggleStatus // FIXED: Add this prop for status toggle
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isInCart, setIsInCart] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  // Update cart state when cart prop changes
  useEffect(() => {
    // Check if this property is in the cart
    const inCart = cart && Object.keys(cart).includes(property.id.toString());
    setIsInCart(inCart);
  }, [cart, property.id]);

  const handleDelete = () => {
     if (window.confirm('Are you sure you want to delete this property?')) {
      onDelete(property.id);
    }
  };

  const handleCartAction = async () => {
    if (isUpdating) return;
    if (!isPropertyActive() && !isInCart) {
      notification.error({
        message: 'Inactive property',
        description: 'Inactive properties cannot be added to cart.',
      });
      return;
    }
    
    setIsUpdating(true);
    
    try {
      if (isInCart) {
        // Call removeFromCart and wait for result
        if (removeFromCart) {
          const result = await removeFromCart(property.id);
          // Don't set local state here - let the cart prop update handle it
          if (!result) {
            // If removal failed, keep current state
            console.error('Failed to remove from cart');
          }
        }
      } else {
        // Call addToFromCart and wait for result
        if (addToFromCart) {
          const result = await addToFromCart(property);
          // Don't set local state here - let the cart prop update handle it
          if (!result) {
            // If adding failed, keep current state
            console.error('Failed to add to cart');
          }
        }
      }
    } catch (error) {
      console.error('Error handling cart action:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleSelect = (e) => {
    if (onSelect) {
      onSelect(e.target.checked);
    }
  };

  // FIXED: Handle status toggle
  const handleStatusToggle = async (e) => {
    e.stopPropagation(); // Prevent event bubbling
    
    if (onToggleStatus) {
      const currentStatus = property.status;
      const newStatus = (currentStatus === 1 || currentStatus === '1') ? 0 : 1;
      await onToggleStatus(property.id, newStatus);
    }
  };

  // FIXED: Determine if property is active
  const isPropertyActive = () => {
    const status = property.status;
    return status === 1 || status === '1' || status === true;
  };

  // FIXED: Get status display color and text
  const getStatusDisplay = () => {
    const active = isPropertyActive();
    return {
      color: active ? 'text-green-600' : 'text-red-600',
      bgColor: active ? 'bg-green-100' : 'bg-red-100',
      text: active ? 'Active' : 'Inactive'
    };
  };

  const statusDisplay = getStatusDisplay();
  const isActive = isPropertyActive();
  const cartActionDisabled = isUpdating || (!isActive && !isInCart);

  return (
    <div 
      className={`border rounded-md shadow property-card transition-all duration-300 relative overflow-hidden ${
        isHovered || isInCart ? 'bg-golden-navy' : 'bg-white'
      }`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{ minHeight: '174px' }}
    >
      {/* Selection checkbox for admin */}
    {/*   {isAdmin && onSelect && (
        <div className="absolute top-1 left-1 z-10">
          <Checkbox 
            checked={isSelected} 
            onChange={handleSelect}
            className={isHovered || isInCart ? "text-white" : ""}
          />
        </div>
      )} */}
      
      {/* FIXED: Property Status Indicator */}
     {/*  <div className={`absolute top-1 right-1 z-10 px-2 py-1 rounded-full text-xs font-medium ${statusDisplay.bgColor} ${statusDisplay.color}`}>
        {statusDisplay.text}
      </div> */}
      
      <div className="h-full flex flex-col p-2">
        {/* Top Action Bar */}
        <div className="flex justify-between items-start mb-1">
          {isHovered && (
            <FiEdit2 
              className="w-5 h-5 cursor-pointer hover:scale-110 transition-transform text-golden-yellow"
              onClick={() => onEdit(property.encrypted_id || property.id)} 
            />
          )}
          
          {/* Center Icon */}
          <div className="flex-1 flex justify-center">
            {!isHovered && (
              <img 
                src={property.property_type_id === 1 ? '/ico/propertyHouseIco.svg' : '/ico/propertyBuildingIco.svg'} 
                alt="Property Icon" 
                className="h-8 w-8"
              />
            )}

            {isHovered && (
              <div className="relative">
                <Tooltip title={!isActive && !isInCart ? 'Inactive properties cannot be added to cart' : ''}>
                  <button
                    onClick={handleCartAction}
                    disabled={cartActionDisabled}
                    className={`flex items-center justify-center bg-golden-yellow text-white rounded-full w-10 h-10 hover:bg-opacity-90 transition-colors duration-300 ${cartActionDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <FiShoppingCart className="w-6 h-6 text-golden-navy" />
                  </button>
                </Tooltip>
                {/* Overlay plus/minus icon */}
                <div className="absolute -top-1 -right-1 bg-golden-yellow rounded-full p-0.5">
                  {isInCart ? (
                    <FiMinusCircle className="w-3.5 h-3.5 text-golden-navy" />
                  ) : (
                    <FiPlusCircle className="w-3.5 h-3.5 text-golden-navy" />
                  )}
                </div>
              </div>
            )}
          </div>

          {isHovered && (
            <div className="flex flex-col space-y-1">
              <FiTrash2 
                className="w-4 h-4 cursor-pointer hover:scale-110 transition-transform text-golden-yellow"
                onClick={handleDelete} 
              />
              {/* FIXED: Status toggle button for admins */}
            {/*   {isAdmin && onToggleStatus && (
                <Tooltip title={`Click to ${isPropertyActive() ? 'deactivate' : 'activate'} property`}>
                  <button
                    onClick={handleStatusToggle}
                    className={`w-4 h-4 rounded-full border-2 ${
                      isPropertyActive() 
                        ? 'bg-green-500 border-green-600' 
                        : 'bg-red-500 border-red-600'
                    } hover:scale-110 transition-transform`}
                  />
                </Tooltip>
              )} */}
            </div>
          )}
        </div>

        {/* Property Info */}
        <div className="text-center flex-1 flex flex-col justify-center min-h-0">
          <h3 className={`text-sm font-semibold truncate px-1 ${isHovered || isInCart ? 'text-golden-yellow' : 'text-golden-yellow'}`}>
            {property.property_title}
          </h3>
          {property.agency_company_name && (
            <p className={`text-xs font-medium truncate px-1 mt-0.5 ${isHovered || isInCart ? 'text-white' : 'text-golden-navy'}`}>
              {property.agency_company_name}
            </p>
          )}
          <p className={`text-xs truncate px-1 mt-0.5 ${isHovered || isInCart ? 'text-gray-300' : 'text-gray-600'}`}>
            {property.apartment_address}
          </p>
          <div className={`text-xs mt-0.5 ${isHovered || isInCart ? 'text-white' : 'text-golden-navy'}`}>
            {property.delivery_zip_code}
          </div>
          
          {/* Show owner info when showOwner is true */}
          {showOwner && ownerName && (
            <div 
              className={`text-xs mt-1 flex items-center justify-center cursor-pointer hover:underline ${isHovered || isInCart ? 'text-gray-300' : 'text-gray-600'}`}
              onClick={(e) => {
                e.stopPropagation();
                if (onImpersonateUser) onImpersonateUser();
              }}
            >
              <FiUser className="mr-1" />
              {ownerName.length > 15 ? ownerName.substring(0, 15) + '...' : ownerName}
            </div>
          )}
        </div>

        {onRecurringOrders && <button type="button" disabled={!isActive}
          aria-label={`Recurring orders for ${property.property_title}`}
          onClick={() => onRecurringOrders(property)}
          className={`mt-2 rounded border px-2 py-1 text-xs disabled:opacity-50 ${isHovered || isInCart ? 'border-golden-yellow text-golden-yellow' : 'border-gray-200 text-golden-navy'}`}>
          Recurring orders
        </button>}
        <div className="md:hidden mt-2 grid grid-cols-3 gap-1 border-t border-gray-200 pt-2">
          <button
            type="button"
            aria-label="Edit property"
            onClick={() => onEdit && onEdit(property.encrypted_id || property.id)}
            className={`flex h-9 items-center justify-center rounded border text-sm ${
              isHovered || isInCart ? 'border-golden-yellow text-golden-yellow' : 'border-gray-200 text-golden-navy'
            }`}
          >
            <FiEdit2 className="h-4 w-4" />
          </button>
          <button
            type="button"
            aria-label={isInCart ? 'Remove from cart' : 'Add to cart'}
            onClick={handleCartAction}
            disabled={cartActionDisabled}
            className={`flex h-9 items-center justify-center rounded border text-sm ${
              isHovered || isInCart ? 'border-golden-yellow text-golden-yellow' : 'border-gray-200 text-golden-navy'
            } ${cartActionDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {isInCart ? <FiMinusCircle className="h-4 w-4" /> : <FiPlusCircle className="h-4 w-4" />}
          </button>
          <button
            type="button"
            aria-label="Delete property"
            onClick={handleDelete}
            className={`flex h-9 items-center justify-center rounded border text-sm ${
              isHovered || isInCart ? 'border-golden-yellow text-golden-yellow' : 'border-gray-200 text-red-600'
            }`}
          >
            <FiTrash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default PropertyCard;
